import React, { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import { KeyRound, Eye, EyeOff, ShieldAlert, LogOut } from 'lucide-react';

/**
 * Màn hình bắt buộc đổi mật khẩu lần đầu đăng nhập.
 * Hiển thị che toàn bộ ứng dụng khi user.must_change_password === true
 * (sau khi mật khẩu được hệ thống/admin reset hoặc đồng bộ).
 * Người dùng không thể dùng hệ thống cho đến khi đổi mật khẩu thành công.
 */
export default function ForcePasswordChange() {
  const { user, changePassword, logout } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) { toast.error('Vui lòng nhập đầy đủ mật khẩu'); return; }
    if (newPassword.length < 8) { toast.error('Mật khẩu mới phải có ít nhất 8 ký tự'); return; }
    if (newPassword !== confirmPassword) { toast.error('Mật khẩu xác nhận không khớp'); return; }
    if (newPassword === currentPassword) { toast.error('Mật khẩu mới phải khác mật khẩu hiện tại'); return; }

    setSubmitting(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result.success) {
        toast.success('Đổi mật khẩu thành công! Chào mừng bạn đến với hệ thống.');
        // user.must_change_password đã được set false trong store → gate tự mở
      } else {
        toast.error(result.message || 'Lỗi đổi mật khẩu');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-5 flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Đổi mật khẩu bắt buộc</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Mật khẩu của bạn vừa được hệ thống cấp lại. Vì lý do bảo mật, vui lòng đặt mật khẩu mới
              trước khi tiếp tục sử dụng.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {user && (
            <div className="bg-gray-50 rounded-lg px-4 py-2.5 text-sm text-gray-600">
              Tài khoản: <span className="font-semibold text-gray-900">{user.full_name || user.email}</span>
              {user.employee_id && <span className="ml-1 text-gray-400">({user.employee_id})</span>}
            </div>
          )}

          {/* Current password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Mật khẩu hiện tại (được cấp) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Mật khẩu hệ thống đã cấp"
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Mật khẩu mới <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Ít nhất 8 ký tự"
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button type="button" onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Xác nhận mật khẩu mới <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="Nhập lại mật khẩu mới"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Mật khẩu xác nhận không khớp</p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !currentPassword || !newPassword || newPassword !== confirmPassword}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Đang xử lý...
              </>
            ) : (
              <>
                <KeyRound className="w-5 h-5" /> Đổi mật khẩu &amp; tiếp tục
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 py-1"
          >
            <LogOut className="w-4 h-4" /> Đăng xuất
          </button>
        </form>
      </div>
    </div>
  );
}
